import React, { useEffect, useId, useRef, useState } from "react";

/**
 * Behavior for the card's inline disconnect confirm panel: reveal, focus into
 * the panel's target element, Escape/Cancel closes it and returns focus to the
 * button that opened it, and a confirmed action can close it WITHOUT restoring
 * focus (the trigger button's own busy/disabled state takes over visually, so
 * there's nothing to return focus to).
 */
function useRevealPanel() {
  const [revealed, setRevealed] = useState(false);
  const triggerRef = useRef(null);
  const focusTargetRef = useRef(null);
  const restoreFocusRef = useRef(false);

  useEffect(() => {
    if (revealed) {
      focusTargetRef.current?.focus();
    } else if (restoreFocusRef.current) {
      restoreFocusRef.current = false;
      triggerRef.current?.focus();
    }
  }, [revealed]);

  function reveal() {
    setRevealed(true);
  }

  // Closes and returns focus to the trigger — Cancel, Escape.
  function cancel() {
    restoreFocusRef.current = true;
    setRevealed(false);
  }

  // Closes without restoring focus — a confirmed action whose own busy state
  // replaces what the trigger looked like.
  function dismiss() {
    setRevealed(false);
  }

  return { revealed, reveal, cancel, dismiss, triggerRef, focusTargetRef };
}

// Statuses where the source still exists but the authorization no longer works
// (079 connection_status). Each gets its own label, because "expired" and
// "revoked" call for the same action but mean different things to the merchant.
const ATTENTION_COPY = {
  expired: {
    label: "Authorization expired",
    detail: (name) => `Reconnect ${name} to resume syncing orders.`,
  },
  revoked: {
    label: "Access revoked",
    detail: (name) => `Access was removed in ${name}. Reconnect to resume syncing orders.`,
  },
  error: {
    label: "Connection error",
    detail: (name) => `Reconnect ${name} to resume syncing orders.`,
  },
};

function formatLastSynced(dateString) {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return null;
  const day = date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const time = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${day}, ${time}`;
}

// Backfill progress for an authorized connection. Kept separate from the
// status badge: 079 tracks authorization and sync progress as two lifecycles.
function syncLineFor(connection) {
  switch (connection.sync_state) {
    case "pending_backfill":
    case "backfilling":
      return { text: "Importing order history", tone: "muted" };
    case "failed":
      return { text: connection.last_error ? `Sync failed. ${connection.last_error}` : "Sync failed", tone: "error" };
    case "live": {
      const formatted = formatLastSynced(connection.last_sync_at);
      return formatted ? { text: `Last synced ${formatted}`, tone: "muted" } : null;
    }
    default:
      return null;
  }
}

// Stroke icons match the 12px, 2px-stroke set used by the GA4 card's buttons.
const ICONS = {
  link: (
    <>
      <path d="M15 7h3a5 5 0 0 1 5 5 5 5 0 0 1-5 5h-3m-6 0H6a5 5 0 0 1-5-5 5 5 0 0 1 5-5h3" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </>
  ),
  refresh: (
    <>
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </>
  ),
  power: (
    <>
      <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
      <line x1="12" y1="2" x2="12" y2="12" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 15 14" />
    </>
  ),
  alert: (
    <>
      <circle cx="12" cy="12" r="9" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </>
  ),
};

function Icon({ name, paths, size = 12, strokeWidth = 2 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      {paths ? paths.map((d) => <path key={d} d={d} />) : ICONS[name]}
    </svg>
  );
}

const baseButton = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  padding: "6px 12px",
  borderRadius: 8,
  fontSize: 12,
  whiteSpace: "nowrap",
  fontFamily: "inherit",
};

// One style per button variant. `unavailable` is a fixed, always-inert look
// (not a faded primary: white on pale pink fails contrast and reads as
// broken); every other variant dims and shows a "default" cursor while busy.
const BUTTON_VARIANTS = {
  primary: { padding: "6px 14px", border: "none", fontWeight: 600, background: "#F90091", color: "#fff", minWidth: 125 },
  unavailable: { border: "1px solid #E2E2E2", fontWeight: 500, background: "#F5F5F5", color: "#626262", minWidth: 125 },
  // Matches the GA4 card's Disconnect: dark outline, not the grey "cancel" style.
  outline: { border: "1px solid #151515", fontWeight: 600, background: "none", color: "#151515", minWidth: 125 },
  quiet: { border: "1px solid #E2E2E2", fontWeight: 500, background: "#FFFFFF", color: "#3E3E3E" },
  solidDark: { border: "1px solid #151515", fontWeight: 600, background: "#151515", color: "#fff" },
};

function buttonStyle(variant, busy = false) {
  if (variant === "unavailable") {
    return { ...baseButton, ...BUTTON_VARIANTS.unavailable, cursor: "not-allowed" };
  }
  return {
    ...baseButton,
    ...BUTTON_VARIANTS[variant],
    cursor: busy ? "default" : "pointer",
    opacity: busy ? 0.6 : 1,
  };
}

// Shared by the card's footer strips (confirm prompt, disconnect confirm, post-connect step).
const STRIP = {
  borderTop: "1px solid #F0F0F0",
  background: "#FAFAFA",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  flexWrap: "wrap",
};

function StatusLine({ dotColor, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: dotColor, display: "inline-block", flexShrink: 0 }} />
      <span style={{ fontSize: 12, color: "#3E3E3E" }}>{label}</span>
    </div>
  );
}

/**
 * One card, presentational, for one sales source: either a Connect card
 * (no connection, or one whose status is "disconnected") or a card for one
 * connection. `source` comes from salesSources.js; `connection` is a row from
 * sales_integrations.integration_connections (or null). `actionError` is the
 * full, already-composed message for a failed disconnect or a failed connect
 * start, shown on this card so a failure never hides the others.
 * `connectBlockedNote`, when set, disables Connect and shows that note under
 * it (a support user on a `memberOnlyConnect` source).
 */
export function ConnectCard({ source, connection, onConnectClick, onDisconnect, disconnecting, connecting, actionError, resultNote, onDismissResult, confirmPrompt, connectBlockedNote = null }) {
  const disconnectPanel = useRevealPanel();
  const regionSelectId = useId();
  const blockedNoteId = useId();
  // The source's optional region choice (`connectRegions`), first option by default.
  const regions = source.connectRegions;
  const [region, setRegion] = useState(regions?.[0]?.value);

  const status = connection?.status;
  const attention = ATTENTION_COPY[status];
  const cardState = status === "connected" ? "connected" : attention ? "attention" : "none";
  const hasFlow = typeof source.startConnect === "function";
  const syncLine = cardState === "connected" ? syncLineFor(connection) : null;
  const connectVerb = cardState === "attention" ? "Reconnect" : "Connect";
  const connectIcon = cardState === "attention" ? "refresh" : "link";
  const busy = disconnecting || connecting;

  // A source with `appStore` config (Shopify) never asks the merchant to type
  // anything: reconnect uses the connection's own stored value, and when there
  // is none (or no connection at all) the card offers the App Store action.
  const reconnectValue = cardState === "attention" ? source.reconnectValue?.(connection) : undefined;
  const appStore = source.appStore;
  // Hidden while a confirm prompt is showing: the merchant already installed
  // from Shopify, so "install from the App Store" would contradict the prompt.
  const showAppStore = cardState !== "connected" && hasFlow && appStore && !reconnectValue && !confirmPrompt;
  const listingUrl = showAppStore ? appStore.getListingUrl?.() : undefined;

  // Optional per-source "one more step" strip (see `postConnectStep` in
  // salesSources.js): the step itself, or one quiet line once the source says
  // it is done. Hidden behind anything that needs the merchant's attention
  // first.
  const step = source.postConnectStep;
  const showStep =
    step && cardState === "connected" && !confirmPrompt && !actionError && resultNote?.tone !== "error" && !disconnectPanel.revealed;
  const stepState = showStep ? step.getState?.(connection) ?? "todo" : null;
  const stepUrl = showStep && stepState !== "done" ? step.getUrl(connection) : undefined;

  function handleConfirmDisconnect() {
    disconnectPanel.dismiss();
    onDisconnect();
  }

  // A region choice applies to a fresh Connect only: a reconnect reuses the
  // connection's own stored value.
  const showRegions = Boolean(regions?.length) && !reconnectValue && !connectBlockedNote;

  function handleConnectOrReconnectClick() {
    if (!hasFlow || connectBlockedNote) return;
    if (reconnectValue) onConnectClick(reconnectValue);
    else if (showRegions) onConnectClick(region);
    else onConnectClick();
  }

  const borderColor = cardState === "connected" ? "#F9009133" : cardState === "attention" ? "#F59E0B66" : "#E2E2E2";

  return (
    <div
      style={{
        background: "#FFFFFF",
        border: `1px solid ${borderColor}`,
        borderRadius: 12,
        overflow: "hidden",
        marginTop: 12,
      }}
    >
      <div style={{ padding: "14px 16px", display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: 10,
            background: source.color.bg,
            border: `1px solid ${source.color.border}`,
            color: source.color.text,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon paths={source.iconPaths} size={20} strokeWidth={1.8} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 600, color: "#151515", fontSize: 14, lineHeight: 1.2 }}>{source.name}</span>
            <span
              style={{
                fontSize: 9,
                fontWeight: 600,
                letterSpacing: 0.4,
                textTransform: "uppercase",
                color: "#626262",
                background: "#F5F5F5",
                padding: "2px 6px",
                borderRadius: 4,
              }}
            >
              Sales
            </span>
          </div>

          {cardState !== "none" && connection.external_account_id && (
            // The account id is what tells two stores of one source apart, so
            // it gets its own full-width line rather than sharing one with the
            // status next to the action column.
            <div
              title={connection.external_account_id}
              style={{
                fontSize: 12,
                color: "#3E3E3E",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                marginBottom: 3,
              }}
            >
              {connection.external_account_id}
            </div>
          )}

          {cardState === "connected" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <StatusLine dotColor="#208a3e" label="Connected" />
              {syncLine && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 4,
                    fontSize: 11,
                    lineHeight: 1.4,
                    // #c62828, not the style guide's #e53935: #e53935 is
                    // ~4.2:1 on white, below AA for text this size (11-13px).
                    color: syncLine.tone === "error" ? "#c62828" : "#626262",
                  }}
                >
                  {syncLine.tone === "error" && (
                    <span style={{ display: "inline-flex", paddingTop: 2 }}>
                      <Icon name="alert" size={11} />
                    </span>
                  )}
                  <span>{syncLine.text}</span>
                </div>
              )}
            </div>
          )}

          {cardState === "attention" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <StatusLine dotColor="#F59E0B" label={attention.label} />
              <span style={{ fontSize: 11, color: "#626262", lineHeight: 1.4 }}>
                {status === "error" && connection.last_error ? connection.last_error : attention.detail(source.name)}
              </span>
            </div>
          )}
        </div>

        <div style={{ flexShrink: 0, paddingTop: 2, display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
          {/* No primary action while a confirm prompt is showing: the
              prompt's own Connect is the only way forward. */}
          {cardState !== "connected" &&
            !confirmPrompt &&
            (showAppStore ? (
              listingUrl ? (
                <a
                  href={listingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={appStore.actionAriaLabel ?? appStore.actionLabel}
                  style={{ ...buttonStyle("primary"), textDecoration: "none" }}
                >
                  <Icon name="link" />
                  {appStore.actionLabel}
                </a>
              ) : null
            ) : hasFlow ? (
              <>
                {showRegions && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <label htmlFor={regionSelectId} style={{ fontSize: 11, color: "#626262" }}>
                      Region
                    </label>
                    <select
                      id={regionSelectId}
                      aria-label={`${source.name} region`}
                      value={region}
                      onChange={(e) => setRegion(e.target.value)}
                      disabled={busy}
                      style={{
                        maxWidth: 130,
                        padding: "4px 6px",
                        borderRadius: 8,
                        border: "1px solid #E2E2E2",
                        background: "#FFFFFF",
                        color: "#151515",
                        fontSize: 12,
                        fontFamily: "inherit",
                      }}
                    >
                      {regions.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleConnectOrReconnectClick}
                  disabled={busy || Boolean(connectBlockedNote)}
                  aria-label={`${connectVerb} ${source.name}`}
                  aria-describedby={connectBlockedNote ? blockedNoteId : undefined}
                  style={connectBlockedNote ? buttonStyle("unavailable") : buttonStyle("primary", busy)}
                >
                  <Icon name={connectIcon} />
                  {connectVerb}
                </button>
                {connectBlockedNote && (
                  <span id={blockedNoteId} style={{ fontSize: 11, color: "#626262", lineHeight: 1.4, maxWidth: 160, textAlign: "right" }}>
                    {connectBlockedNote}
                  </span>
                )}
              </>
            ) : (
              <button
                type="button"
                disabled
                aria-label={`${connectVerb} ${source.name}, available soon`}
                style={buttonStyle("unavailable")}
              >
                <Icon name="clock" />
                Available soon
              </button>
            ))}

          {cardState !== "none" && !disconnectPanel.revealed && (
            <button
              ref={disconnectPanel.triggerRef}
              type="button"
              onClick={disconnectPanel.reveal}
              disabled={disconnecting}
              aria-label={disconnecting ? `Disconnecting ${source.name}` : `Disconnect ${source.name}`}
              style={buttonStyle("outline", disconnecting)}
            >
              <Icon name="power" />
              {disconnecting ? "Disconnecting…" : "Disconnect"}
            </button>
          )}
        </div>
      </div>

      {confirmPrompt && (
        // DEV-2332: an install-origin pending confirm (Shopify itself
        // started the OAuth flow before this merchant signed in to
        // Audiohook). Explicit, never automatic — an attacker could send
        // their own store's confirm link to an Audiohook user, so the
        // merchant must knowingly opt in to binding it to THIS advertiser.
        <div
          role="group"
          aria-label={`Connect ${confirmPrompt.subject} to ${confirmPrompt.advertiserName}`}
          style={{ ...STRIP, background: "#FFF5FB", padding: "12px 16px" }}
        >
          <div style={{ minWidth: 0, flex: "1 1 220px" }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#151515" }}>
              Connect {confirmPrompt.subject} to {confirmPrompt.advertiserName}?
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            <button
              type="button"
              onClick={confirmPrompt.onNotNow}
              disabled={confirmPrompt.busy}
              style={buttonStyle("quiet", confirmPrompt.busy)}
            >
              Not now
            </button>
            <button
              type="button"
              onClick={confirmPrompt.onConnect}
              disabled={confirmPrompt.busy}
              aria-label={`Confirm connecting ${confirmPrompt.subject} to ${confirmPrompt.advertiserName}`}
              style={buttonStyle("primary", confirmPrompt.busy)}
            >
              {confirmPrompt.busy ? "Connecting…" : "Connect"}
            </button>
          </div>
        </div>
      )}

      {disconnectPanel.revealed && (
        <div
          role="group"
          aria-label={`Confirm disconnecting ${source.name}`}
          onKeyDown={(e) => {
            if (e.key === "Escape") disconnectPanel.cancel();
          }}
          style={{ ...STRIP, padding: "12px 16px" }}
        >
          <div style={{ minWidth: 0, flex: "1 1 180px" }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#151515" }}>Disconnect {source.name}?</div>
            <div style={{ fontSize: 11, color: "#626262", marginTop: 2, lineHeight: 1.4 }}>
              Order syncing stops. Data already imported is kept.
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            <button ref={disconnectPanel.focusTargetRef} type="button" onClick={disconnectPanel.cancel} disabled={disconnecting} style={buttonStyle("quiet", disconnecting)}>
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmDisconnect}
              disabled={disconnecting}
              aria-label={`Confirm disconnect ${source.name}`}
              style={buttonStyle("solidDark", disconnecting)}
            >
              Disconnect
            </button>
          </div>
        </div>
      )}

      {actionError && !disconnectPanel.revealed && (
        <div
          role="alert"
          style={{
            borderTop: "1px solid #fca5a5",
            background: "#FFF5F5",
            padding: "10px 16px",
            display: "flex",
            alignItems: "flex-start",
            gap: 6,
            // #c62828, not the style guide's #e53935: #e53935 is ~4.2:1 on
            // white, below AA for text this size (11-13px).
            color: "#c62828",
            fontSize: 12,
            lineHeight: 1.4,
          }}
        >
          <span style={{ paddingTop: 1 }}>
            <Icon name="alert" />
          </span>
          <span>{actionError}</span>
        </div>
      )}

      {!actionError && resultNote && !disconnectPanel.revealed && (
        <div
          role={resultNote.tone === "error" ? "alert" : "status"}
          style={{
            borderTop: `1px solid ${resultNote.tone === "error" ? "#fca5a5" : "#BBF7D0"}`,
            background: resultNote.tone === "error" ? "#FFF5F5" : "#F0FDF4",
            padding: "10px 16px",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 6,
            // Same #c62828 as actionError above; success uses the style
            // guide's green accent (#16A34A).
            color: resultNote.tone === "error" ? "#c62828" : "#16A34A",
            fontSize: 12,
            lineHeight: 1.4,
          }}
        >
          <span>{resultNote.text}</span>
          {typeof onDismissResult === "function" && (
            <button
              type="button"
              onClick={onDismissResult}
              aria-label={`Dismiss ${source.name} connection result`}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                margin: 0,
                cursor: "pointer",
                color: "inherit",
                fontSize: 14,
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              ×
            </button>
          )}
        </div>
      )}

      {cardState === "none" && (
        <div style={{ borderTop: "1px solid #F0F0F0", padding: "10px 16px" }}>
          <span style={{ fontSize: 12, color: "#626262", lineHeight: 1.5 }}>{source.description}</span>
        </div>
      )}

      {showStep && (
        <div role="group" aria-label={stepState === "done" ? step.doneLabel : step.heading} style={{ ...STRIP, padding: "10px 16px" }}>
          {stepState === "done" ? (
            <StatusLine dotColor="#208a3e" label={step.doneLabel} />
          ) : (
            <>
              <div style={{ minWidth: 0, flex: "1 1 220px" }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#151515" }}>{step.heading}</div>
                <div style={{ fontSize: 11, color: "#626262", marginTop: 2, lineHeight: 1.4 }}>{stepUrl ? step.body : step.fallbackBody}</div>
                {stepState === "pending" && step.delayNote && (step.delayNoteApplies?.(connection, Date.now()) ?? true) && (
                  <div style={{ fontSize: 11, color: "#626262", marginTop: 2, lineHeight: 1.4 }}>{step.delayNote}</div>
                )}
              </div>
              {stepUrl && (
                <a
                  href={stepUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={step.linkAriaLabel}
                  style={{ ...buttonStyle("quiet"), textDecoration: "none", color: "#F90091", flexShrink: 0 }}
                >
                  <Icon name="link" />
                  {step.linkLabel}
                </a>
              )}
            </>
          )}
        </div>
      )}

      {showAppStore && !listingUrl && (
        <div style={{ borderTop: "1px solid #F0F0F0", padding: "10px 16px" }}>
          <span style={{ fontSize: 12, color: "#3E3E3E", lineHeight: 1.5 }}>{appStore.guidance}</span>
        </div>
      )}
    </div>
  );
}
